import type {
  DeactivatedProfilesStatsResponse,
  ExclusionKeyword,
  RestoreProfilesResponse,
  ScanExclusionsResponse,
  ScanPreviewResponse,
} from '@shared/schemas';
import { all, call, debounce, getContext, put, select, takeLatest } from 'redux-saga/effects';

import { logger } from '@/utils/logger';

import type { DeactivatedFilters } from '../domain/deactivated-filters';
import type {
  DeactivatedProfilesGateway,
  DeactivatedProfilesPage,
} from '../infrastructure/deactivated-profiles.gateway';
import {
  deactivatedRequested,
  filtersChanged,
  keywordAddRequested,
  keywordDeleteRequested,
  keywordsRequested,
  pageChanged,
  pageSizeChanged,
  profileTypesRequested,
  reactivatedPageChanged,
  reactivatedPageSizeChanged,
  reactivatedRequested,
  restoreRequested,
  scanPreviewRequested,
  scanRequested,
  statsRequested,
} from './deactivated-profiles.actions';
import {
  selectFilters,
  selectPage,
  selectPageSize,
  selectReactivated,
} from './deactivated-profiles.selectors';
import { deactivatedProfilesActions } from './deactivated-profiles.slice';

const LOAD_ERROR = 'Failed to load deactivated profiles.';
const REACTIVATED_LOAD_ERROR = 'Failed to load reactivated profiles.';
const RESTORE_ERROR = 'Failed to restore profiles.';
const SCAN_ERROR = 'Failed to scan for excluded profiles.';
const KEYWORDS_ERROR = 'Failed to load exclusion keywords.';
const KEYWORD_MUTATION_ERROR = 'Failed to update the keyword.';

/** Best-effort human message for the failure banner / snackbar (no domain error union). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * WORKER: fetch the current page. Reads `page` / `pageSize` back from the store
 * (the single source of truth for the fetch) and calls the gateway. `takeLatest`
 * cancels an in-flight fetch when a newer trigger arrives; the abort-latest
 * behaviour the old `useInfiniteApi` had.
 */
function* fetchCurrentPage() {
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');
  const page: number = yield select(selectPage);
  const pageSize: number = yield select(selectPageSize);
  const filters: DeactivatedFilters = yield select(selectFilters);

  yield put(deactivatedProfilesActions.loadStarted());
  try {
    const result = (yield call(gateway.fetchDeactivated, {
      page,
      pageSize,
      filters,
    })) as DeactivatedProfilesPage;
    yield put(deactivatedProfilesActions.loadSucceeded(result));
  } catch (e) {
    yield put(deactivatedProfilesActions.loadFailed(toMessage(e, LOAD_ERROR)));
  }
}

/**
 * WORKER: fetch the header aggregates. No `loadStarted`, and the failure branch
 * carries no message: the band is supporting information, so it fails quietly
 * rather than raising a banner over a table that loaded.
 */
function* fetchStats() {
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');
  try {
    const stats = (yield call(gateway.fetchStats)) as DeactivatedProfilesStatsResponse;
    yield put(deactivatedProfilesActions.statsLoadSucceeded(stats));
  } catch {
    yield put(deactivatedProfilesActions.statsLoadFailed());
  }
}

/**
 * WORKER: fetch the Reactivated tab's current page. Same shape as
 * `fetchCurrentPage`, on its own cursor: the tabs paginate independently.
 */
function* fetchReactivatedPage() {
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');
  const { page, pageSize }: ReturnType<typeof selectReactivated> = yield select(selectReactivated);

  yield put(deactivatedProfilesActions.reactivatedLoadStarted());
  try {
    const result = (yield call(gateway.fetchReactivated, {
      page,
      pageSize,
    })) as DeactivatedProfilesPage;
    yield put(deactivatedProfilesActions.reactivatedLoadSucceeded(result));
  } catch (e) {
    yield put(
      deactivatedProfilesActions.reactivatedLoadFailed(toMessage(e, REACTIVATED_LOAD_ERROR)),
    );
  }
}

/** WORKER: move the Reactivated tab to a page, then fetch it. */
function* reactivatedPageChangedWorker(action: ReturnType<typeof reactivatedPageChanged>) {
  yield put(deactivatedProfilesActions.reactivatedPageSet(action.payload));
  yield* fetchReactivatedPage();
}

/** WORKER: change the Reactivated tab's page size (resets to page 0), then fetch. */
function* reactivatedPageSizeChangedWorker(action: ReturnType<typeof reactivatedPageSizeChanged>) {
  yield put(deactivatedProfilesActions.reactivatedPageSizeSet(action.payload));
  yield* fetchReactivatedPage();
}

/** WORKER: move to a page, then fetch it. */
function* pageChangedWorker(action: ReturnType<typeof pageChanged>) {
  yield put(deactivatedProfilesActions.pageSet(action.payload));
  yield* fetchCurrentPage();
}

/**
 * WORKER: refetch after the toolbar changed.
 *
 * Debounced at the root rather than throttled per control: the search box emits
 * on every keystroke, and a dropdown paying the same 300ms is imperceptible
 * where a second code path would not be.
 */
function* filtersChangedWorker() {
  // No transition to dispatch: the slice applies `filtersChanged` itself, so the
  // controlled search box keeps up with the typing. This is only the fetch.
  yield* fetchCurrentPage();
}

/** WORKER: change the page size (resets to page 0), then fetch. */
function* pageSizeChangedWorker(action: ReturnType<typeof pageSizeChanged>) {
  yield put(deactivatedProfilesActions.pageSizeSet(action.payload));
  yield* fetchCurrentPage();
}

/**
 * WORKER: bulk-restore, then refresh the page AND the aggregates. The success
 * count feeds the page's snackbar; the refetch drops the restored rows from the
 * list, and restoring moves the total and the last-deactivation date, so leaving
 * the band alone would leave it stating a number the table has just contradicted.
 *
 * The Reactivated tab is refetched too: the rows that just left this list are
 * exactly the ones that tab now has to show.
 */
function* restoreWorker(action: ReturnType<typeof restoreRequested>) {
  const { ids } = action.payload;
  if (ids.length === 0) return;
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');

  yield put(deactivatedProfilesActions.restoreStarted());
  try {
    const res = (yield call(gateway.restoreProfiles, ids)) as RestoreProfilesResponse;
    yield put(
      deactivatedProfilesActions.restoreSucceeded({
        count: res.restored_count,
        // Optional in the shared shape (the list-scoped endpoint shares it and
        // never immunises); the global restore always returns it.
        immunised: res.immunised_count ?? 0,
      }),
    );
    yield* fetchCurrentPage();
    yield* fetchStats();
    yield* fetchReactivatedPage();
  } catch (e) {
    yield put(deactivatedProfilesActions.restoreFailed(toMessage(e, RESTORE_ERROR)));
  }
}

/**
 * WORKER: run the manual scan, then refresh the page AND the aggregates (exactly
 * like `restoreWorker`): the scan deactivates rows, so the list and the figures
 * both move. The success count feeds the page's snackbar.
 */
function* scanWorker() {
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');

  yield put(deactivatedProfilesActions.scanStarted());
  try {
    const res = (yield call(gateway.scanExclusions)) as ScanExclusionsResponse;
    yield put(
      deactivatedProfilesActions.scanSucceeded({
        count: res.deactivated_count,
        bypassed: res.bypassed_count,
      }),
    );
    yield* fetchCurrentPage();
    yield* fetchStats();
  } catch (e) {
    yield put(deactivatedProfilesActions.scanFailed(toMessage(e, SCAN_ERROR)));
  }
}

/**
 * WORKER: load the preview count for the confirm dialog. Quiet failure (no
 * snackbar): the dialog shows a fallback, and the scan itself is still gated by
 * the explicit confirm.
 */
function* previewWorker() {
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');

  yield put(deactivatedProfilesActions.previewRequested());
  try {
    const res = (yield call(gateway.previewScan)) as ScanPreviewResponse;
    yield put(
      deactivatedProfilesActions.previewSucceeded({
        count: res.match_count,
        bypassed: res.bypassed_count,
      }),
    );
  } catch {
    yield put(deactivatedProfilesActions.previewFailed());
  }
}

/** WORKER: load the blocklist for the manage-keywords dialog. */
function* keywordsWorker() {
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');

  yield put(deactivatedProfilesActions.keywordsLoadStarted());
  try {
    const keywords = (yield call(gateway.fetchExclusionKeywords)) as ExclusionKeyword[];
    yield put(deactivatedProfilesActions.keywordsLoadSucceeded(keywords));
  } catch (e) {
    yield put(deactivatedProfilesActions.keywordsLoadFailed(toMessage(e, KEYWORDS_ERROR)));
  }
}

/**
 * WORKER: the Type filter's options (#702). A failure only costs the filter, so
 * it is logged rather than turned into a banner over a list that loaded fine.
 */
function* profileTypesWorker() {
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');
  try {
    const types = (yield call(gateway.fetchProfileTypes)) as string[];
    yield put(deactivatedProfilesActions.profileTypesLoaded(types));
  } catch (e) {
    logger.warn('[deactivated-profiles] Profile types could not be read', e);
  }
}

/**
 * WORKER: add a keyword, then refetch the list. A duplicate (409) surfaces its
 * API message via `keywordMutationFailed`, shown inline in the dialog.
 */
function* keywordAddWorker(action: ReturnType<typeof keywordAddRequested>) {
  const { word } = action.payload;
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');

  yield put(deactivatedProfilesActions.keywordMutationStarted());
  try {
    yield call(gateway.addExclusionKeyword, word);
    yield* keywordsWorker();
    yield put(deactivatedProfilesActions.keywordMutationSettled());
  } catch (e) {
    yield put(
      deactivatedProfilesActions.keywordMutationFailed(toMessage(e, KEYWORD_MUTATION_ERROR)),
    );
  }
}

/** WORKER: delete a keyword, then refetch the list. */
function* keywordDeleteWorker(action: ReturnType<typeof keywordDeleteRequested>) {
  const { id } = action.payload;
  const gateway: DeactivatedProfilesGateway = yield getContext('deactivatedProfilesGateway');

  yield put(deactivatedProfilesActions.keywordMutationStarted());
  try {
    yield call(gateway.deleteExclusionKeyword, id);
    yield* keywordsWorker();
    yield put(deactivatedProfilesActions.keywordMutationSettled());
  } catch (e) {
    yield put(
      deactivatedProfilesActions.keywordMutationFailed(toMessage(e, KEYWORD_MUTATION_ERROR)),
    );
  }
}

/** WATCHERS. */
export function* deactivatedProfilesSaga() {
  yield all([
    takeLatest(deactivatedRequested.type, fetchCurrentPage),
    takeLatest(statsRequested.type, fetchStats),
    takeLatest(pageChanged.type, pageChangedWorker),
    takeLatest(pageSizeChanged.type, pageSizeChangedWorker),
    debounce(300, filtersChanged.type, filtersChangedWorker),
    takeLatest(restoreRequested.type, restoreWorker),
    takeLatest(scanRequested.type, scanWorker),
    takeLatest(scanPreviewRequested.type, previewWorker),
    takeLatest(keywordsRequested.type, keywordsWorker),
    takeLatest(profileTypesRequested.type, profileTypesWorker),
    takeLatest(keywordAddRequested.type, keywordAddWorker),
    takeLatest(keywordDeleteRequested.type, keywordDeleteWorker),
    takeLatest(reactivatedRequested.type, fetchReactivatedPage),
    takeLatest(reactivatedPageChanged.type, reactivatedPageChangedWorker),
    takeLatest(reactivatedPageSizeChanged.type, reactivatedPageSizeChangedWorker),
  ]);
}
